/**
 * This file contains changes and event listeners for the Cookiebot by Usercentrics that is used in this project.
 */

interface UsercentricsSettings {
  /** Wahr, wenn der aktuelle Benutzer notwendige Cookies akzeptiert hat. Die Eigenschaft ist schreibgeschützt. */
  necessary: boolean;

  /** Wahr, wenn der aktuelle Benutzer Cookies mit Marketingfunktion akzeptiert hat. Die Eigenschaft ist schreibgeschützt. */
  marketing: boolean;

  /** Wahr, wenn der aktuelle Benutzer Cookies mit Statistikfunktion akzeptiert hat. Die Eigenschaft ist schreibgeschützt. */
  statistics: boolean;

  /** Wahr, wenn der aktuelle Benutzer Cookies mit Präferenzfunktion akzeptiert hat. Die Eigenschaft ist schreibgeschützt. */
  preferences: boolean;

  stamp: string;

  /** Die Art und Weise, wie die Einwilligung erteilt wurde. Entweder „indirekt”, wenn die Einwilligung automatisch erteilt wird, oder „ausdrücklich”, wenn sie vom Nutzer erteilt wird. Liegt keine Einwilligung vor, ist der Wert null. Wenn die Methode zur Einwilligung unbekannt ist (alte Einwilligungen), wird sie als „indirekt” betrachtet. Die Eigenschaft ist schreibgeschützt. */
  method: string;
}

export interface Usercentrics {
  /** Function that will open the cookie banner */
  renew: () => void;

  /** Consent */
  consent: UsercentricsSettings;
}

declare global {
  interface Window {
    /** Instance of the Cookiebot by Usercentrics */
    Cookiebot: Usercentrics;
  }
}

/**
 * Do some things with the Cookiebot by Usercentrics
 * @see {@link https://www.cookiebot.com/de/developer/}
 * @example
 *   window.addEventListener('CookiebotOnAccept', function (e) {
 *     // Das Ereignis wird ausgelöst, wenn der Benutzer die Verwendung von Cookies akzeptiert. Das Ereignis wird auch ausgelöst, wenn der Benutzer bei einem früheren Besuch der Website zugestimmt hat.
 *   })
 *
 *   window.addEventListener('CookiebotOnDecline', function (e) {
 *     // Das Ereignis wird ausgelöst, wenn der Benutzer die Verwendung von Cookies, durch Klicken auf den Ablehnungs-Button im Cookie-Zustimmungs-Dialog, ablehnt. Das Ereignis wird auch ausgelöst, wenn der Benutzer bereits bei einem früheren Besuch der Website abgelehnt hat.
 *   })
 *
 *   window.addEventListener('CookiebotOnLoad', function (e) {
 *     // Das Ereignis wird gleichzeitig mit dem window.onload-Ereignis ausgelöst, wenn die Zustimmung des Benutzers geladen wurde – entweder wenn der Benutzer seine Zustimmung abgibt oder wenn der Benutzer zu einer Seite navigiert, auf der die Zustimmung bereits abgegeben wurde.
 *   })
 */
export const userCentrics = async () => {
  if (!window.Cookiebot) {
    console.warn('Cookiebot (Usercentrics) not yet initialized');
    return;
  }

  /** When the banner is displayed */
  window.addEventListener('CookiebotOnDialogDisplay', () => {
    const el = document.getElementById('CybotCookiebotDialog');

    /** The alt attribute on the company logo only reads `"Logo"` by default, which is a bit useless. */
    const companyLogo: HTMLImageElement = el.querySelector(
      '#CybotCookiebotDialogPoweredbyImage'
    );
    if (companyLogo)
      companyLogo.setAttribute('alt', `Logo Neue Energien Hilden`);

    /**
     * Change the alt attribute on the company logo, which is `"Logo"` by default and therefore useless.
     * @see {@link https://support.cookiebot.com/hc/en-us/requests/165849}
     */
    const cookiebotLogo: HTMLAnchorElement = el.querySelector(
      '#CybotCookiebotDialogPoweredbyCybot'
    );
    if (cookiebotLogo)
      cookiebotLogo.setAttribute(
        'aria-label',
        'Powered by Cookiebot by Usercentrics. Visit cookiebot.com'
      );

    /** Focus on the banner */
    el.focus();
  });

  /**
   * Elements that will open the cookie banner on click
   */
  const triggers: HTMLAnchorElement[] = Array.from(
    document.querySelectorAll(`[href="#cookie-settings"]`)
  );

  triggers.forEach((trigger) => {
    /** Open cookie banner */
    const onTrigger = (e: MouseEvent) => {
      e.preventDefault();

      window.Cookiebot.renew();
    };

    trigger.addEventListener('click', onTrigger);
  });

  const handleEscBanner = () => {
    const CybotCookiebotDialog: HTMLElement | null = document.getElementById(
      'CybotCookiebotDialog'
    );
    const CybotCookiebotDialogBodyButtons: HTMLElement | null =
      document.getElementById('CybotCookiebotDialogBodyButtons');

    if (!CybotCookiebotDialog || !CybotCookiebotDialogBodyButtons) {
      return;
    }

    CybotCookiebotDialogBodyButtons.setAttribute(
      'aria-label',
      'Cookie Auswahl'
    );

    CybotCookiebotDialogBodyButtons.setAttribute('tabindex', '-1');

    const onKeydown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        if (e.repeat) return;

        alert('Bitte wählen Sie eine der drei Cookie-Optionen.');
        CybotCookiebotDialogBodyButtons.focus();
      }
    };

    if (CybotCookiebotDialog) {
      document.addEventListener('keydown', onKeydown);
    }
  };

  handleEscBanner();
};

/**
 * Fire a callback event when the configuration of Usercentrics is changed/updated/loaded. We can react to the current state of consent.
 * @example
 *   userCentricsOnUpdate(() => {
 *     if (window.Cookiebot.consent.marketing) {
 *       // marketing is allowed
 *     } else {
 *       // marketing is not allowed
 *     }
 *   })
 */
export const userCentricsOnUpdate = (callback: () => void) => {
  window.addEventListener('CookiebotOnLoad', () => {
    callback.call(this);
  });

  window.addEventListener('CookiebotOnAccept', () => {
    callback.call(this);
  });

  window.addEventListener('CookiebotOnDecline', () => {
    callback.call(this);
  });
};

/**
 * Check if the consent for a {@link cookieGroup} is given. The consent will always be given on non-production environments.
 * @param cookieGroup Cookie group
 * @returns `true` when the consent is given
 */
export const userCentricsConsentGiven = (cookieGroup: string) => {
  return (
    (window.Cookiebot && window.Cookiebot.consent[cookieGroup]) ||
    document.documentElement.dataset.env !== 'production'
  );
};
